import React, { useState, useEffect } from 'react';
import { sellersApi, categoriesApi } from '../../services/apiService';
import './SupplierEditModal.css';

const SupplierEditModal = ({ isOpen, onClose, supplier, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    businessType: 'Manufacturer',
    category: '',
    country: '',
    city: '',
    address: '',
    zipCode: '',
    email: '',
    phoneNumber: '',
    websiteLink: '',
    linkedInUrl: '',
    description: '',
    companyOverview: '',
    establishYear: '',
    licenseNumber: '',
    numberOfEmployees: '',
    serviceArea: '',
    annualSales: '',
    totalCapitalization: '',
    logo: '',
    coverImage: '',
    status: 'Pending',
    membershipPlan: 'Basic',
    verificationLevel: 'pending',
    isGlobal: false,
    isVerified: false,
    memberYears: 1
  });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesApi.getAll();
        setCategories(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Load supplier data when modal opens
  useEffect(() => {
    if (supplier && isOpen) {
      setFormData({
        name: supplier.name || '',
        businessType: supplier.businessType || 'Manufacturer',
        category: supplier.category || '',
        country: supplier.country || '',
        city: supplier.city || '',
        address: supplier.address || '',
        zipCode: supplier.zipCode || '',
        email: supplier.email || '',
        phoneNumber: supplier.phoneNumber || '',
        websiteLink: supplier.websiteLink || '',
        linkedInUrl: supplier.linkedInUrl || '',
        description: supplier.description || '',
        companyOverview: supplier.companyOverview || '',
        establishYear: supplier.establishYear || '',
        licenseNumber: supplier.licenseNumber || '',
        numberOfEmployees: supplier.numberOfEmployees || '',
        serviceArea: supplier.serviceArea || '',
        annualSales: supplier.annualSales || '',
        totalCapitalization: supplier.totalCapitalization || '',
        logo: supplier.logo || '',
        coverImage: supplier.coverImage || '',
        status: supplier.status || 'Pending',
        membershipPlan: supplier.membershipPlan || 'Basic',
        verificationLevel: supplier.verificationLevel || 'pending',
        isGlobal: supplier.isGlobal || false,
        isVerified: supplier.isVerified || false,
        memberYears: supplier.memberYears || 1
      });
    }
  }, [supplier, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!supplier?.id) {
      alert('Supplier ID not found');
      return;
    }

    try {
      setSaving(true);
      console.log('Updating supplier with data:', formData);
      console.log('Supplier ID:', supplier.id);
      await sellersApi.updateProfileAdmin(supplier.id, formData);
      alert('Supplier updated successfully!');
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (err) {
      console.error('Error updating supplier:', err);
      console.error('Error response:', err.response?.data);
      alert('Failed to update supplier: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const businessTypes = ['Manufacturer', 'Distributor', 'Wholesaler', 'Retailer', 'Importer', 'Exporter', 'Trading Company'];
  const statuses = ['published', 'pending_approval', 'approved', 'draft', 'unpublished', 'archived', 'rejected'];
  const membershipPlans = ['Basic', 'Premium', 'Enterprise'];
  const verificationLevels = ['pending', 'verified', 'inspected', 'certified'];

  return (
    <div className="supplier-edit-modal-overlay" onClick={onClose}>
      <div className="supplier-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="supplier-edit-header">
          <h2>Edit Supplier</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="supplier-edit-tabs">
          <button 
            className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic Info
          </button>
          <button 
            className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
            onClick={() => setActiveTab('company')}
          >
            Company Details
          </button>
          <button 
            className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact Info
          </button>
          <button 
            className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        <form onSubmit={handleSubmit} className="supplier-edit-form">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="form-section">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Business Type</label>
                <select name="businessType" value={formData.businessType} onChange={handleInputChange}>
                  {businessTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select name="category" value={formData.category} onChange={handleInputChange}>
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>License Number</label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Establish Year</label>
                <input
                  type="text"
                  name="establishYear"
                  value={formData.establishYear}
                  onChange={handleInputChange}
                  placeholder="e.g., 2010"
                />
              </div>
              <div className="form-group">
                <label>Number of Employees</label>
                <input
                  type="text"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group full-width">
                <label>Company Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Brief description about your company..."
                />
              </div>

              <div className="form-group full-width">
                <label>Company Overview</label>
                <textarea
                  name="companyOverview"
                  value={formData.companyOverview}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Detailed overview of your company..."
                />
              </div>
            </div>
          )}

          {/* Company Details Tab */}
          {activeTab === 'company' && (
            <div className="form-section">
              <div className="form-group">
                <label>Service Area</label>
                <input
                  type="text"
                  name="serviceArea"
                  value={formData.serviceArea}
                  onChange={handleInputChange}
                  placeholder="e.g., Dubai, Saudi Arabia, Qatar"
                />
              </div>
              <div className="form-group">
                <label>Annual Sales</label>
                <input
                  type="text"
                  name="annualSales"
                  value={formData.annualSales}
                  onChange={handleInputChange}
                  placeholder="e.g., $3.2M USD"
                />
              </div>

              <div className="form-group">
                <label>Total Capitalization</label>
                <input
                  type="text"
                  name="totalCapitalization"
                  value={formData.totalCapitalization}
                  onChange={handleInputChange}
                  placeholder="e.g., $12M"
                />
              </div>
              <div className="form-group">
                <label>Member Years</label>
                <input
                  type="number"
                  name="memberYears"
                  value={formData.memberYears}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="text"
                  name="logo"
                  value={formData.logo}
                  onChange={handleInputChange}
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div className="form-group">
                <label>Cover Image URL</label>
                <input
                  type="text"
                  name="coverImage"
                  value={formData.coverImage}
                  onChange={handleInputChange}
                  placeholder="https://example.com/cover.png"
                />
              </div>

              {formData.logo && (
                <div className="image-preview full-width">
                  <label>Logo Preview:</label>
                  <img src={formData.logo} alt="Logo Preview" />
                </div>
              )}
            </div>
          )}

          {/* Contact Info Tab */}
          {activeTab === 'contact' && (
            <div className="form-section">
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group full-width">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Full address"
                />
              </div>

              <div className="form-group">
                <label>Zip Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="+971 XX XXX XXXX"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="text"
                  name="websiteLink"
                  value={formData.websiteLink}
                  onChange={handleInputChange}
                  placeholder="https://www.example.com"
                />
              </div>

              <div className="form-group full-width">
                <label>LinkedIn URL</label>
                <input
                  type="text"
                  name="linkedInUrl"
                  value={formData.linkedInUrl}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/company/..."
                />
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="form-section">
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  {statuses.map(status => (
                    <option key={status} value={status}>{
                      { published: 'Published', pending_approval: 'Pending Approval', approved: 'Approved', draft: 'Draft', unpublished: 'Unpublished', archived: 'Archived', rejected: 'Rejected' }[status] || status
                    }</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Membership Plan</label>
                <select name="membershipPlan" value={formData.membershipPlan} onChange={handleInputChange}>
                  {membershipPlans.map(plan => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Verification Level</label>
                <select name="verificationLevel" value={formData.verificationLevel} onChange={handleInputChange}>
                  {verificationLevels.map(level => (
                    <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" />

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="isGlobal"
                    checked={formData.isGlobal}
                    onChange={handleInputChange}
                  />
                  <span>Global Supplier</span>
                </label>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="isVerified"
                    checked={formData.isVerified}
                    onChange={handleInputChange}
                  />
                  <span>Verified Supplier</span>
                </label>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierEditModal;
